import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { FileText, Upload, Settings, Mail, AlertTriangle, ArrowLeft, Download, ExternalLink } from 'lucide-react';
import type { LambdaProcessingType } from '@/types';
import { adminBatchService } from '@/services/adminBatchService';
import { logger, ValidationError, NetworkError, ProcessingError } from '@/utils/logger';

type BatchState = 'idle' | 'processing' | 'success' | 'error';

interface ProcessLog {
  timestamp: string;
  message: string;
  type: 'info' | 'success' | 'error';
}

export const BatchCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [batchState, setBatchState] = useState<BatchState>('idle');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [operation, setOperation] = useState<LambdaProcessingType>('validate');
  const [adminEmail, setAdminEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<ProcessLog[]>([]);
  const [result, setResult] = useState<{
    jobId: string;
    totalRecords: number;
    successfulRecords: number;
    failedRecords: number;
    emailSent: boolean;
    message: string;
    csvDownloadUrl?: string;
    jsonDownloadUrl?: string;
    expiresAt?: string;
  } | null>(null);

  const maxFileSize = 50 * 1024 * 1024; // 50MB limit

  const addLog = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    const newLog: ProcessLog = {
      timestamp: new Date().toLocaleTimeString(),
      message,
      type
    };
    setLogs(prev => [...prev, newLog]);
  };

  const resetState = () => {
    setBatchState('idle');
    setCsvFile(null);
    setResult(null);
    setError(null);
    setLogs([]);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.size > maxFileSize) {
      setError(`File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds maximum of 50MB`);
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setCsvFile(file);
    setError(null);
    addLog(`File selected: ${file.name} (${(file.size/1024).toFixed(1)}KB)`, 'info');
  }, [maxFileSize]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    multiple: false,
    disabled: batchState === 'processing'
  });

  const handleProcessBatch = async () => {
    if (!csvFile || !adminEmail.includes('@')) {
      setError('Please select CSV file and enter valid email');
      return;
    }

    setBatchState('processing');
    setError(null);
    setResult(null);
    setLogs([]);

    addLog('Starting internal batch processing...', 'info');
    addLog(`File: ${csvFile.name} (${(csvFile.size/1024).toFixed(1)}KB)`, 'info');
    addLog(`Operation: ${operation}`, 'info');
    addLog(`Admin Email: ${adminEmail}`, 'info');

    try {
      const batchResult = await adminBatchService.processInternalBatch(
        csvFile,
        operation,
        adminEmail
      );

      addLog('Processing completed successfully!', 'success');
      setResult(batchResult);
      setBatchState('success');

    } catch (err) {
      logger.error('Admin batch processing failed:', err);
      
      let errorMessage = 'Unknown error occurred';
      if (err instanceof ValidationError) {
        errorMessage = `Validation Error: ${err.message}`;
      } else if (err instanceof NetworkError) {
        errorMessage = `Network Error: ${err.message} (Status: ${err.status})`;
      } else if (err instanceof ProcessingError) {
        errorMessage = `Processing Error: ${err.message}`;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      addLog(`Error: ${errorMessage}`, 'error');
      setError(errorMessage);
      setBatchState('error');
    }
  };

  const operationOptions: { value: LambdaProcessingType; label: string; description: string }[] = [
    { value: 'validate', label: 'Validate', description: 'Identity verification only' },
    { value: 'enrich', label: 'Enrich', description: 'Contact data enhancement' },
    { value: 'enrich_validate', label: 'Enrich + Validate', description: 'Combined enrichment and validation' },
    { value: 'scoring', label: 'Scoring', description: 'AI collectability scoring' },
    { value: 'scoring_enrich', label: 'Scoring + Enrich', description: 'Combined scoring and enrichment' }
  ];

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/admin/batches')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Create New Batch</h1>
          <p className="text-slate-600 mt-1">Process CSV files through internal batch system</p>
        </div>
      </div>

      {/* Configuration Form */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <h2 className="text-lg font-medium text-slate-900 mb-6">Batch Configuration</h2>
        
        <div className="grid md:grid-cols-2 gap-3">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">
              <FileText className="w-4 h-4 inline mr-2" />
              CSV File
            </label>
            
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
                isDragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : csvFile 
                    ? 'border-green-400 bg-green-50'
                    : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
              } ${batchState === 'processing' ? 'pointer-events-none opacity-50' : ''}`}
            >
              <input {...getInputProps()} />
              <Upload className={`w-8 h-8 mx-auto mb-2 ${csvFile ? 'text-green-600' : 'text-slate-400'}`} />
              <p className="text-sm font-medium text-slate-700">
                {csvFile ? csvFile.name : 'Drop CSV file here or click to browse'}
              </p>
              {csvFile && (
                <p className="text-xs text-green-600 mt-1">
                  {(csvFile.size/1024).toFixed(1)}KB
                </p>
              )}
              <p className="text-xs text-slate-500 mt-2">
                Max 50MB • CSV format only
              </p>
            </div>
          </div>

          {/* Admin Email */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">
              <Mail className="w-4 h-4 inline mr-2" />
              Admin Email (for results)
            </label>
            <input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="admin@company.com"
              disabled={batchState === 'processing'}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Operation Selection */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            <Settings className="w-4 h-4 inline mr-2" />
            Processing Operation
          </label>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {operationOptions.map((option) => (
              <label
                key={option.value}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  operation === option.value
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                } ${batchState === 'processing' ? 'pointer-events-none opacity-50' : ''}`}
              >
                <input
                  type="radio"
                  name="operation"
                  value={option.value}
                  checked={operation === option.value}
                  onChange={(e) => setOperation(e.target.value as LambdaProcessingType)}
                  className="sr-only"
                />
                <div className="font-medium text-slate-900 mb-1">{option.label}</div>
                <div className="text-xs text-slate-600">{option.description}</div>
              </label>
            ))}
          </div>
        </div>

        {/* Process Button */}
        <button
          onClick={handleProcessBatch}
          disabled={batchState === 'processing' || !csvFile || !adminEmail}
          className="w-full mt-6 ca-btn ca-btn--primary font-medium disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors"
        >
          {batchState === 'processing' ? 'Processing...' : 'Process Batch'}
        </button>
      </div>

      {/* Process Logs */}
      <AnimatePresence>
        {logs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-slate-900 border border-slate-700 rounded-lg p-4"
          >
            <h3 className="text-lg font-medium text-white mb-4">Process Log</h3>
            <div className="space-y-2 font-mono text-sm max-h-60 overflow-y-auto">
              {logs.map((log, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-start gap-3 ${
                    log.type === 'success' 
                      ? 'text-green-400' 
                      : log.type === 'error'
                      ? 'text-red-400'
                      : 'text-slate-300'
                  }`}
                >
                  <span className="text-slate-500 flex-shrink-0">{log.timestamp}:</span>
                  <span>{log.message}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Display */}
      <AnimatePresence>
        {error && batchState === 'error' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-red-900 mb-2">Processing Failed</h3>
                <p className="text-red-700 mb-4">{error}</p>
                <button
                  onClick={resetState}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Display */}
      <AnimatePresence>
        {result && batchState === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4"
          >
            <h3 className="text-lg font-medium text-green-900 mb-4">Batch Created Successfully</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <div className="text-lg font-semibold text-green-700">{result.jobId}</div>
                <div className="text-sm text-green-600">Job ID</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <div className="text-lg font-semibold text-green-700">{result.totalRecords}</div>
                <div className="text-sm text-green-600">Records</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <div className="text-lg font-semibold text-green-700">{result.successfulRecords}</div>
                <div className="text-sm text-green-600">Successful</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <div className="text-lg font-semibold text-green-700">{result.failedRecords}</div>
                <div className="text-sm text-green-600">Failed</div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-4 border border-green-200 mb-4">
              <div className="flex items-center justify-center gap-2 text-green-700">
                <Mail className="w-5 h-5" />
                <span className="font-medium">
                  Email Sent: {result.emailSent ? '✅ Yes' : '❌ No'}
                </span>
              </div>
            </div>
            
            <p className="text-green-700 mb-6">{result.message}</p>
            
            {/* Download Section */}
            {(result.csvDownloadUrl || result.jsonDownloadUrl) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-shrink-0">
                    <Download className="w-6 h-6 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 mb-1">Results Ready for Download</h4>
                    <p className="text-slate-700 text-sm">
                      Processed results are available in multiple formats
                      {result.expiresAt && (
                        <span className="ml-1">
                          (links expire in 1 hour)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  {result.csvDownloadUrl && (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            window.open(result.csvDownloadUrl, '_blank');
                          } catch (error) {
                            logger.error('Failed to download CSV:', error);
                            alert('CSV download failed. Please check your email for results or contact support.');
                          }
                        }}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Download CSV Results
                      </button>
                      <button
                        onClick={() => navigator.clipboard.writeText(result.csvDownloadUrl!)}
                        className="flex items-center gap-2 bg-green-100 hover:bg-green-200 text-green-700 px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Copy CSV Link
                      </button>
                    </>
                  )}
                  {result.jsonDownloadUrl && (
                    <>
                      <button
                        onClick={() => window.open(result.jsonDownloadUrl, '_blank')}
                        className="flex items-center gap-2 ca-btn ca-btn--primary font-medium transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Download JSON Results
                      </button>
                      <button
                        onClick={() => navigator.clipboard.writeText(result.jsonDownloadUrl!)}
                        className="flex items-center gap-2 ca-btn ca-btn--outline font-medium transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Copy JSON Link
                      </button>
                    </>
                  )}
                </div>
                
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800 text-sm">
                    ⚠️ <strong>Download Links Generated!</strong> If downloads fail, please check your email for results. The backend file generation may still be in progress.
                  </p>
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={resetState}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Process Another Batch
              </button>
              <button
                onClick={() => navigate('/admin/batches')}
                className="bg-slate-600 hover:bg-slate-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                View All Batches
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

